/**
 * Fuente única de SEO (sin DOM): la usan tanto el runtime (seoDocument.ts)
 * como el prerender de build (vite.config.ts) para que el HTML que ve Google
 * y el que ve el usuario coincidan exactamente.
 */

export const SITE_ORIGIN = "https://popistant.es";
export const BRAND = "Popistant";
export const OG_IMAGE = `${SITE_ORIGIN}/og-image.png?v=3`;
export const TWITTER_HANDLE = "@popistant";

/**
 * Campo semántico real (no relleno): términos de alta intención que la gente
 * busca en el ámbito del blog indie en español. Se usa en el meta keywords y
 * en el JSON-LD (keywords/about). Mantener natural y sin repetir en exceso.
 */
export const SITE_KEYWORDS = [
  "música indie",
  "música indie en español",
  "blog de música indie",
  "magazine música indie",
  "grupos indie españoles",
  "bandas indie españolas",
  "nueva ola indie",
  "indie pop",
  "indie rock",
  "novedades música indie",
  "lanzamientos indie 2026",
  "actualidad música indie",
  "noticias música indie",
  "conciertos indie",
  "festivales indie España",
  "crónicas de conciertos",
  "reseñas de discos indie",
  "entrevistas grupos indie",
  "descubrimientos musicales",
  "playlists indie Spotify",
  "novedades indie español",
  "música independiente",
].join(", ");

/** Entidades/temas para schema.org `about`. */
export const SITE_ABOUT = [
  { "@type": "Thing", name: "Música indie" },
  { "@type": "Thing", name: "Indie pop" },
  { "@type": "Thing", name: "Música independiente" },
  { "@type": "Thing", name: "Grupos indie españoles" },
];

export type RouteSeo = {
  /** Título de la página SIN la marca (se compone con composeTitle). */
  pageTitle: string;
  /** Título final ya compuesto (lo rellena resolveRouteSeo). */
  title: string;
  description: string;
  canonical: string;
  /** Encabezado visible para crawlers/no-JS (noscript). */
  h1: string;
  /** Párrafo introductorio rico en keywords para el bloque noscript. */
  intro: string;
  robots?: string;
  ogImage?: string;
  /** Tipo de schema.org para el JSON-LD de la página. */
  pageType?: "WebSite" | "CollectionPage" | "Blog" | "ContactPage" | "WebPage";
};

/** Recorta título para pestaña / SERP (~62 caracteres útiles). */
export function truncateSeoTitle(text: string, max = 62): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trimEnd()}…`;
}

/** Recorta descripción (~155 caracteres). */
export function truncateSeoDescription(text: string, max = 155): string {
  const t = text.trim().replace(/\s+/g, " ");
  if (t.length <= max) return t;
  return `${t.slice(0, max - 3).trimEnd()}...`;
}

/** Patrón "Página | Marca" (la home solo lleva marca + claim corto). */
export function composeTitle(pageTitle: string): string {
  const clean = pageTitle.trim();
  if (!clean || clean === BRAND) {
    return `${BRAND} — Blog de música indie pop, conciertos y playlists`;
  }
  return truncateSeoTitle(`${clean} | ${BRAND}`);
}

function canonicalHref(pathname: string, search = ""): string {
  return `${SITE_ORIGIN}${pathname}${search}`;
}

/**
 * Rutas estáticas indexables. El prerender genera un HTML por cada una con
 * estos metadatos ya escritos (sin depender de JS).
 */
export const STATIC_ROUTES: Array<{
  path: string;
  pageTitle: string;
  description: string;
  h1: string;
  intro: string;
  pageType: NonNullable<RouteSeo["pageType"]>;
}> = [
  {
    path: "/",
    pageTitle: BRAND,
    description:
      "Blog de música indie en español: novedades, grupos indie españoles, conciertos, festivales, reseñas y playlists indie en Spotify. Actualidad indie a diario.",
    h1: "Popistant — blog de música indie en español",
    intro:
      "Popistant es un blog y magazine de música indie y pop independiente en español. Cubrimos la actualidad del indie nacional e internacional: novedades y lanzamientos, grupos indie españoles y nueva ola, crónicas de conciertos y festivales, reseñas de discos, entrevistas, descubrimientos musicales y playlists curadas en Spotify. Si buscas dónde escuchar y leer sobre música indie en España, este es tu sitio.",
    pageType: "WebSite",
  },
  {
    path: "/feed",
    pageTitle: "Conciertos indie",
    description:
      "Conciertos y festivales de música indie en España: crónicas, fotos y directos de grupos indie españoles e internacionales. Agenda indie en Popistant.",
    h1: "Conciertos y festivales de música indie",
    intro:
      "Crónicas, fotos y vídeos de conciertos de música indie en España: festivales, salas y giras de grupos indie españoles y bandas internacionales. Sigue la agenda de conciertos indie y los mejores directos de la escena independiente con Popistant.",
    pageType: "CollectionPage",
  },
  {
    path: "/articulos",
    pageTitle: "Artículos de música indie",
    description:
      "Artículos de música indie en español: reseñas de discos, entrevistas a grupos indie, novedades y reportajes sobre la escena independiente. Blog Popistant.",
    h1: "Artículos y reseñas de música indie",
    intro:
      "Artículos de música indie con voz propia: reseñas de discos y singles, entrevistas a grupos indie españoles y emergentes, novedades, lanzamientos y reportajes sobre la escena musical independiente en España y fuera. Lectura indie de fondo en el blog Popistant.",
    pageType: "Blog",
  },
  {
    path: "/blogs",
    pageTitle: "Blog retro indie",
    description:
      "Archivo y blog retro de música indie: posts históricos sobre indie pop, conciertos y grupos indie españoles recuperados en Popistant.",
    h1: "Blog retro de música indie",
    intro:
      "El archivo retro del blog de música indie Popistant: posts históricos sobre indie pop, grupos indie españoles, conciertos y descubrimientos musicales recuperados para seguir leyéndolos hoy.",
    pageType: "Blog",
  },
  {
    path: "/playlists",
    pageTitle: "Playlists indie en Spotify",
    description:
      "Playlists de música indie en Spotify: novedades indie español, descubrimientos y listas mensuales para escuchar grupos indie nuevos. Selección Popistant.",
    h1: "Playlists de música indie en Spotify",
    intro:
      "Playlists de música indie en Spotify seleccionadas por Popistant: novedades indie español, descubrimientos semanales y listas mensuales para escuchar los mejores grupos indie nuevos, nacionales e internacionales.",
    pageType: "CollectionPage",
  },
  {
    path: "/contacto",
    pageTitle: "Contacto",
    description:
      "Contacta con Popistant: prensa, propuestas de conciertos, envío de maquetas de música indie y colaboraciones con el blog de música indie en español.",
    h1: "Contacto",
    intro:
      "Escribe a Popistant para prensa, propuestas de conciertos y festivales, envío de maquetas y discos de música indie o colaboraciones con el blog de música indie en español.",
    pageType: "ContactPage",
  },
  {
    path: "/aviso-legal",
    pageTitle: "Aviso legal",
    description: "Información legal del sitio web Popistant (popistant.es), blog de música indie pop.",
    h1: "Aviso legal",
    intro: "Información legal y titularidad del sitio web Popistant.",
    pageType: "WebPage",
  },
  {
    path: "/privacidad",
    pageTitle: "Política de privacidad",
    description: "Política de privacidad y protección de datos del blog de música indie Popistant.",
    h1: "Política de privacidad",
    intro: "Cómo trata Popistant los datos personales y la privacidad de los visitantes.",
    pageType: "WebPage",
  },
  {
    path: "/cookies",
    pageTitle: "Política de cookies",
    description: "Uso de cookies en el sitio web Popistant, blog de música indie pop.",
    h1: "Política de cookies",
    intro: "Información sobre el uso de cookies en el sitio web Popistant.",
    pageType: "WebPage",
  },
];

/** Enlaces internos para el bloque noscript (anchor text con keywords). */
export const NOSCRIPT_NAV: Array<{ href: string; label: string }> = [
  { href: "/", label: "Inicio — blog de música indie" },
  { href: "/feed", label: "Conciertos indie" },
  { href: "/articulos", label: "Artículos de música indie" },
  { href: "/blogs", label: "Blog retro indie" },
  { href: "/playlists", label: "Playlists indie en Spotify" },
  { href: "/contacto", label: "Contacto" },
];

const NOINDEX_ROUTES: Record<string, string> = {
  "/login": "noindex, nofollow",
  "/admin": "noindex, nofollow",
  "/unsubscribe": "noindex, nofollow",
};

/** SEO por ruta (usado en runtime y en prerender). */
export function resolveRouteSeo(pathname: string, search = ""): RouteSeo {
  const c = canonicalHref(pathname, pathname === "/" ? "" : search);
  const found = STATIC_ROUTES.find((r) => r.path === pathname);

  if (found) {
    return {
      pageTitle: found.pageTitle,
      title: composeTitle(found.pageTitle),
      description: truncateSeoDescription(found.description),
      canonical: pathname === "/" ? canonicalHref("/", "") : c,
      h1: found.h1,
      intro: found.intro,
      pageType: found.pageType,
    };
  }

  if (NOINDEX_ROUTES[pathname]) {
    return {
      pageTitle: pathname === "/admin" ? "Administración" : "Acceso",
      title: composeTitle(pathname === "/admin" ? "Administración" : "Acceso"),
      description: "Área privada de Popistant.",
      canonical: c,
      h1: "Popistant",
      intro: "Área privada de Popistant.",
      robots: NOINDEX_ROUTES[pathname],
      pageType: "WebPage",
    };
  }

  if (pathname.startsWith("/post/")) {
    return {
      pageTitle: "Publicación",
      title: composeTitle("Música indie"),
      description: truncateSeoDescription(
        "Artículo, concierto o playlist de música indie pop en Popistant. Lee la publicación completa en el blog.",
      ),
      canonical: c,
      h1: "Publicación — Popistant",
      intro: "Contenido de música indie pop en el blog Popistant: artículos, conciertos y playlists.",
      pageType: "WebPage",
    };
  }

  return {
    pageTitle: "Página no encontrada",
    title: composeTitle("Página no encontrada"),
    description: "La página solicitada no existe en popistant.es, el blog de música indie pop.",
    canonical: c,
    h1: "Página no encontrada",
    intro: "La página solicitada no existe. Vuelve al blog de música indie Popistant.",
    robots: "noindex",
    pageType: "WebPage",
  };
}

/** JSON-LD (Organization + WebSite con SearchAction + página actual). */
export function buildJsonLd(seo: RouteSeo): string {
  const graph: unknown[] = [
    {
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: BRAND,
      alternateName: ["popistant.es", "Popistant música indie", "blog música indie Popistant"],
      url: `${SITE_ORIGIN}/`,
      logo: OG_IMAGE,
      sameAs: ["https://www.instagram.com/popistant_/", "https://x.com/popistant"],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_ORIGIN}/#website`,
      name: BRAND,
      alternateName: ["popistant.es", "Popistant blog música indie"],
      url: `${SITE_ORIGIN}/`,
      inLanguage: "es-ES",
      description:
        "Blog de música indie en español: novedades, grupos indie españoles, conciertos, festivales, reseñas y playlists indie en Spotify.",
      keywords: SITE_KEYWORDS,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_ORIGIN}/articulos?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "Blog",
      "@id": `${SITE_ORIGIN}/#blog`,
      name: "Popistant — blog de música indie",
      url: `${SITE_ORIGIN}/articulos`,
      inLanguage: "es-ES",
      description:
        "Artículos, reseñas, entrevistas y novedades de música indie en español. Cobertura de grupos indie españoles, conciertos y festivales.",
      keywords: SITE_KEYWORDS,
      about: SITE_ABOUT,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    },
    {
      "@type": seo.pageType ?? "WebPage",
      "@id": `${seo.canonical}#page`,
      url: seo.canonical,
      name: seo.title,
      headline: seo.h1,
      description: seo.description,
      inLanguage: "es-ES",
      isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
      keywords: SITE_KEYWORDS,
      about: SITE_ABOUT,
    },
  ];

  return JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * HTML interior del bloque <!--SEO_HEAD--> (title + metas + JSON-LD).
 * Lo usa el prerender de build para escribir un <head> correcto por ruta.
 */
export function buildHeadInner(seo: RouteSeo): string {
  const t = escapeAttr(seo.title);
  const d = escapeAttr(seo.description);
  const url = escapeAttr(seo.canonical);
  const robots = seo.robots ?? "index, follow, max-image-preview:large, max-snippet:-1";
  const ogImage = escapeAttr(seo.ogImage ?? OG_IMAGE);
  return (
    `<title>${seo.title}</title>` +
    `<meta name="description" content="${d}" />` +
    `<meta name="robots" content="${escapeAttr(robots)}" />` +
    `<meta name="author" content="${BRAND}" />` +
    `<meta name="keywords" content="${escapeAttr(SITE_KEYWORDS)}" />` +
    `<link rel="canonical" href="${url}" />` +
    `<meta property="og:title" content="${t}" />` +
    `<meta property="og:description" content="${d}" />` +
    `<meta property="og:type" content="website" />` +
    `<meta property="og:url" content="${url}" />` +
    `<meta property="og:locale" content="es_ES" />` +
    `<meta property="og:image" content="${ogImage}" />` +
    `<meta property="og:site_name" content="${BRAND}" />` +
    `<meta name="twitter:card" content="summary_large_image" />` +
    `<meta name="twitter:site" content="${TWITTER_HANDLE}" />` +
    `<meta name="twitter:title" content="${t}" />` +
    `<meta name="twitter:description" content="${d}" />` +
    `<meta name="twitter:image" content="${ogImage}" />` +
    `<script type="application/ld+json" id="seo-jsonld">${buildJsonLd(seo)}</script>`
  );
}

/** Bloque noscript: contenido real + enlaces internos para crawlers sin JS. */
export function buildNoscriptHtml(seo: RouteSeo): string {
  const nav = NOSCRIPT_NAV.map(
    (l) => `<li><a href="${l.href}">${l.label}</a></li>`,
  ).join("");
  return (
    `<div id="seo-fallback">` +
    `<h1>${seo.h1}</h1>` +
    `<p>${seo.intro}</p>` +
    `<nav aria-label="Secciones de Popistant"><ul>${nav}</ul></nav>` +
    `<p>Popistant — blog de música indie pop, conciertos, artículos y playlists en Spotify desde España.</p>` +
    `</div>`
  );
}
